import { z } from "zod";

export const GAME_TYPE = {
	PHOTO_ROULETTE: "photo_roulette",
	VOICE_GUESSER: "voice_guesser",
	TRUTH_OR_DARE: "truth_or_dare",
	GAMIFICATION_XP: "gamification_xp",
} as const;

export const GAME_TYPE_VALUES = [
	GAME_TYPE.PHOTO_ROULETTE,
	GAME_TYPE.VOICE_GUESSER,
	GAME_TYPE.TRUTH_OR_DARE,
	GAME_TYPE.GAMIFICATION_XP,
] as const;

export const gameTypeSchema = z.enum(GAME_TYPE_VALUES);
export type GameType = z.infer<typeof gameTypeSchema>;

export const LEADERBOARD_SCOPE = {
	GLOBAL: "global",
	WEEKLY: "weekly",
	MONTHLY: "monthly",
} as const;

export const LEADERBOARD_SCOPE_VALUES = [LEADERBOARD_SCOPE.GLOBAL, LEADERBOARD_SCOPE.WEEKLY, LEADERBOARD_SCOPE.MONTHLY] as const;

export const leaderboardScopeSchema = z.enum(LEADERBOARD_SCOPE_VALUES);
export type LeaderboardScope = z.infer<typeof leaderboardScopeSchema>;

export const leaderboardSeasonSchema = z.object({
	scope: z.enum([LEADERBOARD_SCOPE.WEEKLY, LEADERBOARD_SCOPE.MONTHLY]),
	key: z.string().min(1),
	starts_at: z.number().int().positive(),
	ends_at: z.number().int().positive(),
});

export type LeaderboardSeason = z.infer<typeof leaderboardSeasonSchema>;

export const baseLeaderboardEntrySchema = z.object({
	user_id: z.string(),
	display_name: z.string(),
	avatar_url: z.string().nullable(),
	rank: z.number().int().positive(),
	score: z.number().int().nonnegative(),
	games_played: z.number().int().nonnegative(),
	wins: z.number().int().nonnegative(),
	last_active: z.number().int().positive().nullable(),
});

export type BaseLeaderboardEntry = z.infer<typeof baseLeaderboardEntrySchema>;

export const generalLeaderboardEntrySchema = baseLeaderboardEntrySchema;
export type GeneralLeaderboardEntry = z.infer<typeof generalLeaderboardEntrySchema>;

export const generalLeaderboardResponseSchema = z.object({
	scope: leaderboardScopeSchema,
	season: leaderboardSeasonSchema.nullable(),
	entries: z.array(generalLeaderboardEntrySchema),
	current_user_entry: generalLeaderboardEntrySchema.nullable(),
	generated_at: z.number().int().positive(),
});

export type GeneralLeaderboardResponse = z.infer<typeof generalLeaderboardResponseSchema>;

export const generalLeaderboardQuerySchema = z.object({
	scope: leaderboardScopeSchema,
	limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type GeneralLeaderboardQuery = z.infer<typeof generalLeaderboardQuerySchema>;

export const compareGeneralLeaderboardEntries = (
	left: Pick<BaseLeaderboardEntry, "score" | "wins" | "last_active" | "user_id">,
	right: Pick<BaseLeaderboardEntry, "score" | "wins" | "last_active" | "user_id">,
): number => {
	if (left.score !== right.score) return right.score - left.score;
	if (left.wins !== right.wins) return right.wins - left.wins;
	const leftActive = left.last_active ?? 0;
	const rightActive = right.last_active ?? 0;
	if (leftActive !== rightActive) return rightActive - leftActive;
	return left.user_id.localeCompare(right.user_id);
};

export const rankGeneralLeaderboardEntries = (entries: readonly Omit<GeneralLeaderboardEntry, "rank">[]): GeneralLeaderboardEntry[] =>
	entries
		.map((entry) => ({ ...entry }))
		.sort(compareGeneralLeaderboardEntries)
		.map((entry, index) => ({ ...entry, rank: index + 1 }));

export const groupLeaderboardEntrySchema = baseLeaderboardEntrySchema;
export type GroupLeaderboardEntry = z.infer<typeof groupLeaderboardEntrySchema>;

export const groupLeaderboardResponseSchema = z.object({
	group_id: z.string(),
	scope: leaderboardScopeSchema,
	season: leaderboardSeasonSchema.nullable(),
	entries: z.array(groupLeaderboardEntrySchema),
	current_user_entry: groupLeaderboardEntrySchema.nullable(),
	generated_at: z.number().int().positive(),
});

export type GroupLeaderboardResponse = z.infer<typeof groupLeaderboardResponseSchema>;

export const groupLeaderboardQuerySchema = z.object({
	scope: leaderboardScopeSchema,
	limit: z.coerce.number().int().min(1).max(100).default(50),
});

export type GroupLeaderboardQuery = z.infer<typeof groupLeaderboardQuerySchema>;
